import { useMemo, useState } from 'react';
import { createPortal } from 'react-dom';
import { useQueryClient } from '@tanstack/react-query';
import type { Lang } from '@/data/types';
import { KBIcon } from '@/components/kbIcons';
import { friendlyError } from '@/lib/friendlyError';
import { pushUndo } from '@/lib/actesUndo';
import { actesDuplicates, actesTrashDemande, type ActesDupGroup } from '@/integrations/actesApi';
import { actesEn } from '@/lib/actesCountry';

/**
 * LES DOUBLONS D'UN DOSSIER.
 *
 * The same person's birth certificate asked for twice is the normal accident
 * of a dossier built from a form and then completed by hand. This finds them
 * and PROPOSES — it never decides.
 *
 * Three rules, all of them from the original and all of them load-bearing:
 *  · nothing is ticked in advance. An automatic pre-selection is exactly what
 *    made this tool feel dangerous to use.
 *  · a group can never be emptied. The last unticked row in a group refuses
 *    to be ticked, so "remove the duplicates" can never mean "remove all".
 *  · a demande carrying a received document says so, loudly, before it goes —
 *    deleting it sends that file to the bin with it.
 *
 * Nothing is destroyed either way: what goes, goes to the Corbeille, and the
 * whole batch comes back from one toast.
 */
export function DuplicateFinder({ lang, dossierId }: { lang: Lang; dossierId: string }) {
  const T = (fr: string, en: string) => (lang === 'fr' ? fr : actesEn(en));
  const qc = useQueryClient();
  const [open, setOpen] = useState(false);
  const [loading, setLoading] = useState(false);
  const [scanned, setScanned] = useState(0);
  const [groups, setGroups] = useState<ActesDupGroup[] | null>(null);
  const [selected, setSelected] = useState<Set<string>>(new Set());
  const [working, setWorking] = useState(false);
  const [done, setDone] = useState<number | null>(null);
  const [err, setErr] = useState('');

  const scan = async () => {
    setOpen(true); setLoading(true); setErr(''); setDone(null); setSelected(new Set());
    try {
      const r = await actesDuplicates(dossierId);
      setGroups(r.groups); setScanned(r.scanned);
    } catch (e) { setErr(friendlyError(e, lang)); setGroups([]); }
    setLoading(false);
  };

  /* Per group: how many are ticked, so one more tick can be refused when it
     would leave the group empty. */
  const perGroup = useMemo(() => {
    const m = new Map<string, number>();
    for (const g of groups ?? []) m.set(g.recommended_id, g.items.filter((i) => selected.has(i.id)).length);
    return m;
  }, [groups, selected]);

  const toggle = (key: string, id: string, total: number) => {
    setSelected((prev) => {
      const next = new Set(prev);
      if (next.has(id)) { next.delete(id); return next; }
      if ((perGroup.get(key) ?? 0) >= total - 1) return prev;
      next.add(id);
      return next;
    });
  };

  const docsSelected = useMemo(
    () => (groups ?? []).flatMap((g) => g.items).filter((i) => selected.has(i.id) && i.has_document).length,
    [groups, selected],
  );

  const apply = async () => {
    const ids = [...selected];
    if (!ids.length) return;
    setWorking(true); setErr('');
    /* Each delete is inspected: a refused one reported as a success leaves the
       row on screen after the panel closes, which reads as a bug in the tool. */
    const out = await Promise.all(ids.map(async (id) => {
      try { await actesTrashDemande(id); return { id, ok: true as const }; }
      catch (e) { return { id, ok: false as const, msg: e instanceof Error ? e.message : '' }; }
    }));
    const gone = out.filter((o) => o.ok).map((o) => o.id);
    const bad = out.filter((o) => !o.ok);
    setWorking(false);
    await qc.invalidateQueries({ queryKey: ['actes'] });

    if (bad.length) {
      const detail = bad.find((b) => b.msg)?.msg;
      setErr(`${T(`${bad.length} suppression(s) ont échoué.`, `${bad.length} deletion(s) failed.`)}${detail ? ` — ${detail}` : ''}`);
      setSelected(new Set(bad.map((b) => b.id)));   // keep the failures ticked to retry
      if (!gone.length) return;
    } else {
      setSelected(new Set());
    }

    setDone(gone.length);
    setGroups([]);
    if (gone.length) {
      pushUndo({
        label: T(`${gone.length} demande(s) supprimée(s)`, `${gone.length} request(s) deleted`),
        detail: T('Elles peuvent revenir en un clic.', 'They can come back in one click.'),
        undo: async () => {
          for (const id of gone) await actesTrashDemande(id, true);
          await qc.invalidateQueries({ queryKey: ['actes'] });
        },
      });
    }
    if (!bad.length) window.setTimeout(() => setOpen(false), 1600);
  };

  const reason = (r: string) =>
    r === 'document' ? T('contient un document reçu', 'carries a received document')
      : r === 'fields' ? T('la plus complète', 'the most complete')
        : T('la plus ancienne', 'the oldest');

  return (
    <>
      <button className="btn btn-sm btn-ghost" onClick={() => void scan()}>
        <KBIcon name="layers" size={13} /> {T('Doublons', 'Duplicates')}
      </button>

      {open && createPortal(
        <div className="scrim actes-skin" onClick={() => { if (!working) setOpen(false); }}>
          <div className="modal" onClick={(e) => e.stopPropagation()} style={{ maxWidth: 680 }}>
            <div className="modal-h">
              <div>
                <div className="eyebrow">{T('Doublons', 'Duplicates')}</div>
                <h3>{T('Doublons dans ce dossier', 'Duplicates in this dossier')}</h3>
              </div>
              <button className="rb x" onClick={() => setOpen(false)} disabled={working} aria-label={T('Fermer', 'Close')}>
                <KBIcon name="x" size={14} />
              </button>
            </div>
            <div className="modal-b">

              {loading && <p className="muted-note">{T('Analyse des demandes…', 'Scanning the requests…')}</p>}

              {!loading && done !== null && (
                <div className="empty-state">
                  <div className="ico">✅</div>
                  <h3>{T(`${done} doublon(s) supprimé(s)`, `${done} duplicate(s) deleted`)}</h3>
                  <p>{T('Ils restent récupérables depuis la Corbeille.', 'They stay recoverable from the Trash.')}</p>
                </div>
              )}

              {!loading && done === null && groups?.length === 0 && (
                <div className="empty-state">
                  <div className="ico">🛡️</div>
                  <h3>{T('Aucun doublon détecté', 'No duplicate found')}</h3>
                  <p>{T(`${scanned} demandes analysées.`, `${scanned} requests scanned.`)}</p>
                </div>
              )}

              {!loading && done === null && !!groups?.length && (
                <>
                  <p className="muted-note">
                    {T(`${groups.length} groupe(s) de demandes très proches, sur ${scanned} analysées. Cochez celles à supprimer — rien n'est coché d'avance, et une demande reste toujours dans chaque groupe.`,
                       `${groups.length} group(s) of near-identical requests, out of ${scanned} scanned. Tick the ones to delete — nothing is ticked in advance, and one request always stays in each group.`)}
                  </p>

                  {groups.map((g) => {
                    const ticked = perGroup.get(g.recommended_id) ?? 0;
                    const total = g.items.length;
                    return (
                      <div key={g.recommended_id} className="actes-dup">
                        <div className="actes-dup-h">
                          <span className="actes-list-main">
                            <strong>{g.person}</strong>
                            <em>{(g.type ?? T('Sans type', 'No type'))} · {T(`${total} demandes similaires`, `${total} similar requests`)}</em>
                          </span>
                          <span className={`actes-dup-n ${ticked ? 'on' : ''}`}>
                            {T(`${ticked} cochée(s)`, `${ticked} ticked`)}
                          </span>
                        </div>

                        {g.risky && (
                          <p className="muted-note warn-note">
                            ⚠ {T('Plusieurs de ces demandes portent leur PROPRE document — aucune fusion n’est sûre ici.',
                                  'Several of these carry their OWN document — no merge is safe here.')}
                          </p>
                        )}

                        {g.items.map((i) => {
                          const on = selected.has(i.id);
                          const keep = i.id === g.recommended_id;
                          const blocked = !on && ticked >= total - 1;
                          return (
                            <label key={i.id}
                              className={`actes-dup-row ${on ? 'on' : ''} ${blocked ? 'blocked' : ''}`}
                              title={blocked ? T('Une demande doit rester dans le groupe.', 'One request must stay in the group.') : undefined}>
                              <input type="checkbox" checked={on} disabled={blocked}
                                onChange={() => toggle(g.recommended_id, i.id, total)} />
                              <span className="actes-list-main">
                                <strong>{i.person_concerned}{i.type ? ` · ${i.type}` : ''}</strong>
                                <em>
                                  {[i.status,
                                    new Date(i.created_at).toLocaleDateString(lang === 'fr' ? 'fr-FR' : 'en-GB'),
                                    T(`${i.filled} champs remplis`, `${i.filled} fields filled`),
                                    i.has_document ? (i.file_name ?? T('document', 'document')) : '',
                                  ].filter(Boolean).join(' · ')}
                                </em>
                              </span>
                              {on && i.has_document && (
                                <span className="actes-tag c-amber">{T('avec document', 'with document')}</span>
                              )}
                              {keep && !on && (
                                <span className="actes-tag c-emerald" title={g.reasons.map(reason).join(' · ')}>
                                  ★ {T('à conserver', 'keep')}
                                </span>
                              )}
                            </label>
                          );
                        })}

                        {g.items.some((i) => selected.has(i.id) && i.has_document) && (
                          <p className="muted-note warn-note">
                            ⚠ {T('Une demande cochée porte un document reçu. La supprimer envoie aussi ce document à la corbeille.',
                                  'A ticked request carries a received document. Deleting it sends that document to the bin too.')}
                          </p>
                        )}

                        {!!g.reasons.length && (
                          <p className="muted-note">
                            {T('Recommandation :', 'Recommendation:')} {g.reasons.map(reason).join(' · ')}
                          </p>
                        )}
                      </div>
                    );
                  })}
                </>
              )}

              {err && <p className="muted-note ni-err">⚠ {err}</p>}
            </div>

            {!loading && done === null && !!groups?.length && (
                <div className="modal-f">
                  <span className="actes-dup-foot">
                    <b className={selected.size ? 'on' : ''}>
                      {T(`${selected.size} demande(s) sélectionnée(s)`, `${selected.size} request(s) selected`)}
                    </b>
                    {docsSelected > 0 && (
                      <em>⚠ {T(`${docsSelected} portent un document.`, `${docsSelected} carry a document.`)}</em>
                    )}
                  </span>
                  <span className="spacer" />
                  <button className="btn" onClick={() => setOpen(false)} disabled={working}>
                    {T('Annuler', 'Cancel')}
                  </button>
                  <button className="btn btn-p" onClick={() => void apply()} disabled={working || selected.size === 0}>
                    <KBIcon name="trash" size={13} /> {working ? '…' : T(`Supprimer (${selected.size})`, `Delete (${selected.size})`)}
                  </button>
                </div>
              )}
          </div>
        </div>,
        document.body,
      )}
    </>
  );
}
